import { LazyQueryHookOptions, useLazyQuery } from '@apollo/client';
import { phoenixLegalConsentsListQuery } from '@marriott/mi-legal-graphql';
import { CheckBox } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Dropdown } from '../../molecules/Dropdown';
import { SelectOption } from '../../molecules/Dropdown/Dropdown.types';
import { useAuthStore } from '../../store';
import { CountriesAndCountryCode } from './__mock__/LegalConsents.mock';
import { ConsentError, ConsentErrorType } from './ConsentsList/ConsentsList.types';
import { LegalConsents } from './LegalConsents';
import { LegalConsent, OnConsentChangeParams, PhoenixLegalConsentsListType } from './LegalConsents.types';
import { generateApolloClientHeadersForUXLCalls } from '../../utils/uxlHelper';

const initialConsentsState = {
  list: [] as LegalConsent[], // initial list of consents
  sectionTypes: {},
  error: { type: ConsentErrorType.NONE, message: null } as ConsentError,
  consentsLedger: {}, // or the relevant structure for `consentsLedger`

  // other required state fields
};

const locale = 'en-us';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LegalConsentValidator = (_props: any) => {
  const { sessionId } = useAuthStore();
  const [consentsState, setConsentsState] = useState(initialConsentsState);
  const [countryCode, setCountryCode] = useState('KR');
  const [countryOption, setCountryOption] = useState('non-member-reservation');
  const [isValidateError, setValiDateError] = useState(false);
  const [showDataIspector, setShowDataIspector] = useState(false);
  const [showCustomLoader, setShowCustomLoader] = useState(false);
  const customLoaderInput = (
    <div>
      Loading Consents.....
      <span className="m-spinner-m ml-0"></span>
    </div>
  );
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [showResultPage, setShowResultPage] = useState(false);
  const [getLegalConsentsUxl] = useLazyQuery<PhoenixLegalConsentsListType>(phoenixLegalConsentsListQuery);

  // const getLegalConsents = () => {
  //   const uxlOptions: LazyQueryHookOptions = {
  //     // variables: { locale },
  //     context: {
  //       // uri: 'https://marriott-sites2-qa.adobecqms.net/graphql/execute.json/legal-consents/Locale-specific',
  //       uri: '/graphql/execute.json/legal-consents/getCountryConsents',
  //       headers: {
  //         'x-request-id': `Phoenix-Legal-${sessionId}`,
  //       },
  //     },
  //     fetchPolicy: 'network-only',
  //   };
  //   return getLegalConsentsUxl(uxlOptions);
  // };

  const getLegalConsents = () => {
    const uxlOptions: LazyQueryHookOptions = {
      variables: { locale },
      context: generateApolloClientHeadersForUXLCalls(sessionId, 'phoenixLegalConsentsListQuery', locale),
      fetchPolicy: 'network-only',
    };
    return getLegalConsentsUxl(uxlOptions);
  };

  const handleConsentChange = (params: OnConsentChangeParams) => {
    const { updatedConsentsState } = params;
    setConsentsState(prevState => ({
      ...prevState,
      ...updatedConsentsState,
    }));
  };

  useEffect(() => {
    const getCountryOptionList = async () => {
      const legalConsentsRep = await getLegalConsents();
      // const allConsents = legalConsentsRep.data?.sectionsList.items || [];
      const allConsents =
        legalConsentsRep.data?.content?.legal?.legalConsentsByLocale?.aemData?.data?.sectionsList.items || [];
      const allOptions = allConsents.flatMap(consent => consent.options);
      const uniqueOptions = Array.from(new Set(allOptions));

      const optionsWithLabels = uniqueOptions.map(option => ({
        label: option,
        value: option,
      }));
      return optionsWithLabels;
    };

    const fetchOptions = async () => {
      const fetchedOptions = await getCountryOptionList();
      setOptions(fetchedOptions);
    };

    fetchOptions();
  }, []); // Empty dependency array to ensure this runs only once on mount

  const handleChange = () => {
    setValiDateError(!isValidateError);
  };
  const { control } = useForm({
    defaultValues: {
      countryOrRegion: countryCode,
      consentOption: countryOption,
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setShowResultPage(prev => !prev);
  };

  const onChangeDataIspector = () => {
    setShowDataIspector(prev => !prev);
  };

  const onChangeCustomLoader = () => {
    setShowCustomLoader(prev => !prev);
  };

  return (
    <form onSubmit={handleSubmit} className="legal-form">
      <div className="m-page-class">
        <Controller
          name="countryOrRegion"
          control={control}
          render={({ field }) => (
            <Dropdown
              label="Select Country"
              className={clsx('m-dropdown')}
              options={CountriesAndCountryCode}
              name="countryOrRegion"
              value={field.value}
              handleChange={value => {
                field.onChange(value); // Pass the selected value directly
                setCountryCode(value); // Update the state with the selected value
              }}
              id={'country'}
            />
          )}
        />
        <Controller
          name="consentOption"
          control={control}
          render={({ field }) => (
            <Dropdown
              label="Select Option"
              className={clsx('s-dropdown')}
              options={options}
              name="consentOption"
              value={field.value}
              handleChange={value => {
                field.onChange(value); // Pass the selected value directly
                setCountryOption(value); // Update the state with the selected value
              }}
              id={'option'}
            />
          )}
        />
        <CheckBox
          checkboxId="validate-error"
          checkboxName="validate-error"
          checked={isValidateError}
          onChange={handleChange}
        >
          Validate Errors
        </CheckBox>
        <CheckBox
          checkboxId="show-data-inspector"
          checkboxName="show-data-inspector"
          className="ml-3"
          checked={showDataIspector}
          onChange={onChangeDataIspector}
        >
          Show Data Inspector
        </CheckBox>
        <CheckBox
          checkboxId="show-custom-loader"
          checkboxName="show-custom-loader"
          className="ml-3"
          checked={showCustomLoader}
          onChange={onChangeCustomLoader}
        >
          Show Custom Loader
        </CheckBox>
        <button type="submit" className="m-button m-submit-button-legal ml-5">
          {showResultPage ? 'Back' : 'Next'}
        </button>
      </div>
      <div className="ctp-consents-container">
        {showResultPage ? (
          <ResultPage />
        ) : (
          <div style={{ border: '1px solid grey', marginTop: '30px', borderRadius: '5px' }}>
            <LegalConsents
              sessionId={sessionId}
              countryCode={countryCode}
              locale={locale}
              option={countryOption}
              isValidateErrors={isValidateError}
              consentsState={consentsState}
              customLoader={showCustomLoader ? customLoaderInput : null}
              setConsentsState={setConsentsState}
              onConsentChange={handleConsentChange}
            />
          </div>
        )}
      </div>

      {showDataIspector && <DataInspector consentData={consentsState.list} error={consentsState.error} />}
    </form>
  );
};

function ResultPage() {
  return (
    <div style={{ border: '1px solid blue', marginTop: '30px', borderRadius: '5px' }}>
      <h3>New Component</h3>
    </div>
  );
}

function DataInspector({ consentData, error }: { consentData: unknown; error: ConsentError }) {
  return (
    <div>
      <span>Error: {error.type}</span>
      {consentData ? <pre>{JSON.stringify(consentData, null, 2)}</pre> : <p>No data.</p>}
    </div>
  );
}
